import React, { Component } from 'react';
import { safeParse } from '../utils';
const Parser = safeParse;
import { 
  ChevronIcon2,
  PhoneIcon2,
  MailIcon,
  MapIcon,
  DotsIcon
} from './SVGIcons';

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isClient: false,
      mapsLoaded: false,
      mapsError: false,
      mapsInitialized: false,
      lightboxOpen: false,
      currentImage: null,
      currentImageAlt: ''
    };
    this.mapRefs = {};
  }

  // Методы для lightbox
  openLightbox = (imageSrc, imageAlt) => {
    this.setState({
      lightboxOpen: true,
      currentImage: imageSrc,
      currentImageAlt: imageAlt
    });
    document.body.style.overflow = 'hidden';
  }

  closeLightbox = () => {
    this.setState({
      lightboxOpen: false,
      currentImage: null,
      currentImageAlt: ''
    });
    document.body.style.overflow = 'auto';
  }

  handleKeyDown = (e) => {
    if (!this.state.lightboxOpen) return;
    
    if (e.key === 'Escape') {
      this.closeLightbox();
    }
  }

  componentDidMount() {
    this.setState({ isClient: true });
    this.loadYandexMaps();
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    // Очищаем карты при размонтировании
    if (this.mapRefs) {
      Object.values(this.mapRefs).forEach(map => {
        if (map && map.destroy) {
          try {
            map.destroy();
          } catch (e) {
            console.error('Error destroying map:', e);
          }
        }
      });
    }
    window.removeEventListener('keydown', this.handleKeyDown);
    document.body.style.overflow = 'auto';
  }

  loadYandexMaps() {
    // Проверяем, не загружены ли уже карты
    if (window.ymaps) {
      this.setState({ mapsLoaded: true });
      return;
    }

    // Проверяем, не загружается ли уже скрипт
    if (document.querySelector('script[src*="api-maps.yandex.ru"]')) {
      // Ждем загрузки
      const checkYmaps = setInterval(() => {
        if (window.ymaps) {
          clearInterval(checkYmaps);
          this.setState({ mapsLoaded: true });
        }
      }, 100);
      return;
    }

    // Загружаем Яндекс Карты
    const script = document.createElement('script');
    script.src = 'https://api-maps.yandex.ru/2.1/?apikey=1b1f192d-9460-4afb-8ae3-c8fb7643c1ba&lang=ru_RU&ns=ymaps';
    script.async = true;
    
    script.onload = () => {
      this.setState({ mapsLoaded: true });
    };
    
    script.onerror = () => {
      console.error('Failed to load Yandex Maps');
      this.setState({ mapsError: true });
    };
    
    document.head.appendChild(script);
  }

  // Функция для парсинга координат
  parseCoords = (coordsString) => {
    if (!coordsString) return null;
    const [lat, lng] = coordsString.split(',').map(coord => parseFloat(coord.trim()));
    return [lat, lng];
  }

  // Функция для парсинга нескольких координат (разделенных ||)
  parseMultipleCoords = (coordsString) => {
    if (!coordsString) return [];
    return coordsString.split('||').map(coord => this.parseCoords(coord)).filter(coord => coord !== null);
  }

  // Компонент для отображения карты
  renderMap = (coords, zoom, markers, height = '255px', mapId) => {
    const { isClient, mapsLoaded, mapsError, mapsInitialized } = this.state;
    
    if (!isClient) {
      return this.renderMapPlaceholder(height);
    }

    if (mapsError) {
      return this.renderMapPlaceholder(height, 'Ошибка загрузки карты');
    }

    if (!mapsLoaded) {
      return this.renderMapPlaceholder(height, 'Загрузка карты...');
    }

    if (!coords || markers.length === 0) {
      return this.renderMapPlaceholder(height, 'Координаты не указаны');
    }

    // Создаем уникальный ID для карты
    const uniqueMapId = `map-${mapId}-${Date.now()}`;

    return (
      <div 
        id={uniqueMapId}
        className='w-full h-full rounded-[15px]'
        style={{ height }}
        ref={(el) => {
          if (el && !el.hasChildNodes() && window.ymaps && !mapsInitialized) {
            this.initMap(el, coords, zoom, markers, uniqueMapId);
          }
        }}
      />
    );
  }

  renderMapPlaceholder = (height, message = 'Загрузка карты...') => {
    return (
      <div 
        className='w-full h-full rounded-[15px] bg-[#F5F7F9] flex items-center justify-center'
        style={{ height }}
      >
        <div className='text-[#032432]/60'>{message}</div>
      </div>
    );
  }

  initMap = (container, coords, zoom, markers, mapId) => {
    if (!window.ymaps) return;

    const initMapInstance = () => {
      try {
        // Проверяем, не создана ли уже карта для этого контейнера
        if (this.mapRefs[mapId]) return;

        const map = new window.ymaps.Map(container, {
          center: coords,
          zoom: zoom,
          controls: ['zoomControl']
        });

        // Сохраняем ссылку на карту
        this.mapRefs[mapId] = map;

        // Добавляем метки
        markers.forEach(marker => {
          const placemark = new window.ymaps.Placemark(
            marker.coords,
            {
              hintContent: marker.hint,
              balloonContent: marker.balloon
            },
            {
              preset: 'islands#blueDotIcon',
              balloonCloseButton: true
            }
          );
          map.geoObjects.add(placemark);
        });

        // Если меток больше одной, автоматически подбираем границы карты
        if (markers.length > 1) {
          map.setBounds(map.geoObjects.getBounds(), { 
            checkZoomRange: true,
            zoomMargin: 50 // Добавляем отступы от краев
          });
        }

        // Устанавливаем флаг инициализации
        this.setState({ mapsInitialized: true });

      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    // Если API уже готов, инициализируем сразу
    if (window.ymaps && window.ymaps.Map) {
      initMapInstance();
    } else {
      // Иначе ждем готовности
      window.ymaps.ready(initMapInstance);
    }
  }

  render() {
    const data = this.props?.data;
    const contacts = data?.contacts;
    const pageVars = data?.page_vars;
    const pageVarsImage = data?.page_vars_image;
    const { showWindow } = this.props;
    const { lightboxOpen, currentImage, currentImageAlt } = this.state;

    // Функция для безопасного разбиения строки с телефонами (разделитель "||")
    const splitPhones = (phoneString) => {
        return phoneString?.split('||') || [];
    }

    const dzerzhinskPhones = splitPhones(pageVars?.phones);
    
    // Парсим координаты для карты
    const allCoords = this.parseMultipleCoords(pageVars?.coords);
    
    // Создаем маркеры для карты
    const mapMarkers = [];
    if (allCoords.length > 0) {
      // Офис в Дзержинске
      if (allCoords[0]) {
        mapMarkers.push({
          coords: allCoords[0],
          hint: 'Офис в Дзержинске',
          balloon: pageVars?.buhaddr || '606008, Нижегородская область, г. Дзержинск, улица Урицкого 2А, помещение Б'
        });
      }
      
      // Новое производство
      if (allCoords[1]) {
        mapMarkers.push({
          coords: allCoords[1],
          hint: 'Новое производство',
          balloon: pageVars?.proizvaddr || 'Нижегородская область, г. Дзержинск, Нижегородское шоссе, 6'
        });
      }
      
      // Офис в Москве
      if (allCoords[2]) {
        mapMarkers.push({
          coords: allCoords[2],
          hint: 'Офис в Москве',
          balloon: pageVars?.mosaddr || '127018, Московская область, г. Москва, ул. Складочная, д. 1, стр. 1'
        });
      }
    }

    return (
      <>
        <div className='mt-[70px] xl:mt-[80px] tracking-[-0.04em] leading-none'>
          <div className='container'>
            <div className='flex justify-center xl:justify-start items-center gap-[12px] text-[14px] xl:text-[16px] tracking-[-0.02em]'>
              <div className='inline-flex items-center px-[15px] h-[38px] bg-[#F5F7F9] rounded-full gap-[5px]'>
                <PhoneIcon2 stroke="#032432" />
                <div className='tracking-[-0.04em] text-[#032432]/60'>Контакты</div>
              </div>
            </div>
            
            {/* --- Динамические блоки из page_vars --- */}
            <h2 className='font-medium mt-[20px] xl:mt-[30px] text-[24px] xl:text-[36px] text-center xl:text-start text-[#032432]'>{Parser(pageVars?.title_1)}</h2>
            <div className='mt-[10px] text-[14px] xl:text-[16px] tracking-[-0.02em] text-center xl:text-start text-black/60'>{Parser(pageVars?.text_1)}</div>
            
            {/* Карточки сотрудников */}
            <div className='mt-[20px] grid grid-cols-1 xl:grid-cols-3 gap-[10px]'>
              {(contacts?.cards || []).map( ( card, index ) => (
                <div key={index} className='bg-[#F5F7F9] rounded-[15px] p-[5px] flex items-center gap-[15px]'>
                  <img src={card?.image} alt={card?.name} width={120} height={130} className='h-auto flex-none rounded-[16px]' />
                  <div>
                    <div className='text-[#032432] text-[16px] xl:text-[18px]'>{card?.name}</div>
                    <a href={`mailto:${card?.email}`} className='flex items-center gap-[9px] text-[14px] text-black/60 mt-[12px]'>
                      <MailIcon stroke="#009EE3" />
                      <div>{card?.email}</div>
                    </a>
                    <div className='flex items-center gap-[9px] text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60 mt-[12px]'>
                      <PhoneIcon2 stroke="#009EE3" />
                      {card?.phone2 ?
                        <div><a href={`tel:${card?.phone?.replace(/[^\d+]/g, '')}`}>{card?.phone}</a><br /><a href={`tel:${card?.phone2?.replace(/[^\d+]/g, '')}`}>{card?.phone2}</a></div>
                      :
                        <div><a href={`tel:${card?.phone?.replace(/[^\d+]/g, '')}`}>{card?.phone}</a></div>
                      }
                    </div>
                  </div>
                </div>
              ) )}
            </div>
            
            {/* --- Блок "Офис в Дзержинске" --- */}
            <h2 className='font-medium mt-[70px] xl:mt-[80px] text-[24px] xl:text-[36px] text-center xl:text-start text-[#032432]'>{Parser(pageVars?.title_2)}</h2>
            <div className='flex flex-col xl:flex-row gap-[10px] items-start mt-[20px]'>
              <div className='bg-[#F5F7F9] rounded-[15px] p-[14px] pl-[20px] flex-1'>
                <div className='w-full flex flex-col xl:flex-row items-center justify-between'>
                  <div className='w-full xl:w-auto'>
                    <div className='text-[16px] xl:text-[18px] text-[#032432]'>Телефоны</div>
                    <div className='mt-[10px] text-black/60 flex items-start gap-[9px]'>
                      <PhoneIcon2 stroke="#009EE3" />
                      <div className='w-full xl:w-auto'>
                        {/* Динамический список телефонов из page_vars.phones - по 2 в ряд */}
                        <div className='grid grid-cols-2 gap-x-[10px] gap-y-[5px]'>
                          {dzerzhinskPhones.map((phone, idx) => (
                            <div key={idx} className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'><a href={`tel:${phone?.replace(/[^\d+]/g, '')}`}>{phone}</a></div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='flex xl:hidden mt-[15px] flex-col xl:flex-row items-start justify-start gap-[22px]'>
                    <div className='flex items-start justify-between gap-[22px] w-full xl:w-auto'>
                      <div>
                        <div className='text-[16px] xl:text-[18px] text-[#032432]'>Телефон бухгалтера</div>
                        <div className='flex items-center gap-[8px] mt-[10px]'>
                          <PhoneIcon2 stroke="#009EE3" />
                          <div className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'><a href={`tel:${pageVars?.buhphone?.replace(/[^\d+]/g, '')}`}>{pageVars?.buhphone}</a></div>
                        </div>
                      </div>
                      <div>
                        <div className='text-[16px] xl:text-[18px] text-[#032432]'>E-mail</div>
                        <div className='flex items-center gap-[8px] mt-[10px]'>
                          <MailIcon stroke="#009EE3" />
                          <div className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'>{pageVars?.buhmail}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className='text-[16px] xl:text-[18px] text-[#032432]'>Адрес</div>
                      <div className='flex items-center gap-[8px] mt-[10px]'>
                        <MapIcon className='flex-none' stroke="#009EE3" />
                        <div className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'>{pageVars?.buhaddr}</div>
                      </div>
                    </div>
                  </div>
                  <div className='w-full xl:w-[264px] flex-none mt-[20px] xl:mt-0'>
                    {/* Кнопки с ссылками из данных */}
                    <a href={pageVars?.scheme} className='flex items-center gap-[9px] h-[40px] w-full bg-[#009EE3] text-white rounded-full cursor-pointer justify-center text-[14px]'>
                      <div>Схема проезда ОФИС-СКЛАД</div>
                      <ChevronIcon2 stroke="white" />
                    </a>
                    <a href={pageVars?.marshrut} className='flex items-center gap-[9px] h-[40px] w-full bg-[#032432] text-white rounded-full cursor-pointer justify-center text-[14px] mt-[10px]'>
                      <div>Построить маршрут</div>
                      <ChevronIcon2 stroke="white" />
                    </a>
                  </div>
                  {/* Изображение карты из page_vars_image.m1 - добавлен обработчик клика */}
                  <img 
                    src={pageVarsImage?.m1} 
                    className='flex-none h-auto mt-[20px] xl:mt-0 w-full xl:w-[177px] cursor-pointer transition-transform hover:scale-105 duration-300' 
                    width={177} 
                    height={119} 
                    alt="Карта офиса"
                    onClick={() => this.openLightbox(pageVarsImage?.m1, 'Карта офиса')}
                  />
                </div>
                <div className='hidden xl:flex mt-[15px] flex-col xl:flex-row items-start justify-start gap-[22px]'>
                  <div className='flex items-start justify-between gap-[22px] w-full xl:w-auto'>
                    <div>
                      <div className='text-[16px] xl:text-[18px] text-[#032432]'>Телефон бухгалтера</div>
                      <div className='flex items-center gap-[8px] mt-[10px]'>
                        <PhoneIcon2 stroke="#009EE3" />
                        <div className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'><a href={`tel:${pageVars?.buhphone?.replace(/[^\d+]/g, '')}`}>{pageVars?.buhphone}</a></div>
                      </div>
                    </div>
                    <div>
                      <div className='text-[16px] xl:text-[18px] text-[#032432]'>E-mail</div>
                      <div className='flex items-center gap-[8px] mt-[10px]'>
                        <MailIcon stroke="#009EE3" />
                        <div className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'>{pageVars?.buhmail}</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className='text-[16px] xl:text-[18px] text-[#032432]'>Адрес</div>
                    <div className='flex items-center gap-[8px] mt-[10px]'>
                      <MapIcon className='flex-none' stroke="#009EE3" />
                      <div className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'>{pageVars?.buhaddr}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Блок "Возникли вопросы?" */}
              <div className='bg-cover rounded-[15px] w-full xl:w-[197px] xl:h-[189px] flex-none p-[20px] bg-[#009EE3]'
                style={{
                  backgroundImage: `url("${pageVarsImage?.c1}")`,
                }}
              >
                <div className='text-[18px] text-white'>
                  Возникли<br />
                  вопросы?
                </div>
                <div className='text-[14px] text-white/70 mt-[10px]'>
                  Оставьте заявку и мы оперативно свяжемся с вами
                </div>
                <div onClick={() => showWindow()} className='flex items-center gap-[9px] h-[50px] w-full bg-white text-black rounded-full cursor-pointer justify-center mt-[10px]'>
                  <div>Связаться</div>
                  <ChevronIcon2 stroke="black" />
                </div>
              </div>
            </div>

            {/* --- Блок "Новое производство" --- */}
            <h2 className='font-medium mt-[70px] xl:mt-[80px] text-[24px] xl:text-[36px] text-center xl:text-start text-[#032432]'>{Parser(pageVars?.proizvtitle)}</h2>
            <div className='flex flex-col xl:flex-row gap-[10px] items-start mt-[20px]'>
              <div className='bg-[#F5F7F9] rounded-[15px] p-[20px] flex-1 flex flex-col xl:flex-row items-center justify-between'>
                <div>
                  <div className='text-[16px] xl:text-[18px] text-[#032432]'>Адрес</div>
                  <div className='flex items-center gap-[8px] mt-[10px]'>
                    <MapIcon stroke="#009EE3" />
                    <div className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'>{pageVars?.proizvaddr}</div>
                  </div>
                  <div className='text-[16px] xl:text-[18px] text-[#032432] mt-[20px]'>Телефон</div>
                  <div className='flex items-center gap-[8px] mt-[10px]'>
                    <PhoneIcon2 stroke="#009EE3" />
                    <div className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'><a href={`tel:${pageVars?.proizvphone?.replace(/[^\d+]/g, '')}`}>{pageVars?.proizvphone}</a></div>
                  </div>
                </div>
                <div className='w-full xl:w-[303px] flex-none mt-[20px] xl:mt-0'>
                  <a href={pageVars?.proizvscheme} className='flex items-center gap-[9px] h-[40px] w-full bg-[#009EE3] text-white rounded-full cursor-pointer justify-center text-[14px]'>
                    <div>Схема проезда Новое производство</div>
                    <ChevronIcon2 stroke="white" />
                  </a>
                  <a href={pageVars?.proizvmarshrut} className='flex items-center gap-[9px] h-[40px] w-full bg-[#032432] text-white rounded-full cursor-pointer justify-center text-[14px] mt-[10px]'>
                    <div>Построить маршрут</div>
                    <ChevronIcon2 stroke="white" />
                  </a>
                </div>
              </div>
              {/* Изображение карты из page_vars_image.m2 - добавлен обработчик клика */}
              <img 
                src={pageVarsImage?.m2} 
                className='flex-none h-auto w-full xl:w-[224px] cursor-pointer transition-transform hover:scale-105 duration-300' 
                width={224} 
                height={151} 
                alt="Карта производства"
                onClick={() => this.openLightbox(pageVarsImage?.m2, 'Карта производства')}
              />
            </div>

            {/* --- Блок "Офис в Москве" --- */}
            <div className='mt-[70px] xl:mt-[80px] flex flex-col xl:flex-row items-center xl:items-end justify-between gap-[10px]'>
              <h2 className='font-medium text-[24px] xl:text-[36px] text-[#032432]'>{pageVars?.title4}</h2>
              <div className='inline-flex w-full xl:w-auto items-center px-[15px] h-[38px] bg-[#F5F7F9] rounded-full gap-[5px]'>
                <DotsIcon stroke="#032432" />
                <div className='tracking-[-0.04em] text-[#032432]/60 text-[14px] xl:text-[16px] tracking-[-0.02em]'>Специализирован на продаже <span className='text-[#032432]'>Теплоносителей</span> и <span className='text-[#032432]'>Антифризов</span></div>
              </div>
            </div>
            <div className='flex flex-col xl:flex-row gap-[10px] items-start mt-[20px]'>
              <div className='bg-[#F5F7F9] rounded-[15px] p-[20px] flex-1 flex flex-col xl:flex-row items-center justify-between gap-[20px]'>
                <div className='w-full xl:w-auto'>
                  <div className='text-[16px] xl:text-[18px] text-[#032432]'>Адрес</div>
                  <div className='flex items-center gap-[8px] mt-[10px]'>
                    <MapIcon stroke="#009EE3" />
                    <div className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'>{pageVars?.mosaddr}</div>
                  </div>
                </div>
                <div className='w-full xl:w-auto'>
                  <div className='text-[16px] xl:text-[18px] text-[#032432]'>Телефон</div>
                  <div className='flex items-center gap-[8px] mt-[10px]'>
                    <PhoneIcon2 stroke="#009EE3" />
                    <div className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'>
                      <a href={`tel:${pageVars?.mosphone?.replace(/[^\d+]/g, '')}`}>
                        {pageVars?.mosphone}
                      </a>
                    </div>
                  </div>
                </div>
                <div className='w-full xl:w-auto'>
                  <div className='text-[16px] xl:text-[18px] text-[#032432]'>E-mail</div>
                  <div className='flex items-center gap-[8px] mt-[10px]'>
                    <MailIcon stroke="#009EE3" />
                    <div className='text-[14px] xl:text-[16px] tracking-[-0.02em] text-black/60'>{Parser(pageVars?.mosmail)}</div>
                  </div>
                </div>
                <a href={pageVars?.mosmarshrut} className='flex w-full xl:w-auto whitespace-nowrap items-center gap-[9px] h-[40px] px-[20px] bg-[#009EE3] text-white rounded-full cursor-pointer justify-center text-[14px] xl:text-[16px] tracking-[-0.02em]'>
                  <div>Построить маршрут</div>
                  <ChevronIcon2 stroke="white" />
                </a>
              </div>
            </div>

            {/* --- Блок "Карта партнера" --- */}
            <div className='mt-[70px] xl:mt-[80px] flex flex-col xl:flex-row items-center xl:items-end justify-between gap-[20px]'>
              <h2 className='font-medium text-[24px] xl:text-[36px] text-center xl:text-start text-[#032432]'>{pageVars?.title5}</h2>
              <a href={contacts?.rekviziti} className='inline-flex justify-center w-full xl:w-auto items-center px-[15px] h-[38px] border-[1px] border-[rgba(3,36,50,0.1)] rounded-full gap-[5px]'>
                <div className='tracking-[-0.04em] text-[#032432] text-[14px] xl:text-[16px] tracking-[-0.02em]'>Скачать реквизиты</div>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.99219 8.75977L8.91219 10.6798L10.8322 8.75977" stroke="#032432" strokeWidth="1.125" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8.91016 3V10.6275" stroke="#032432" strokeWidth="1.125" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15 9.13477C15 12.4498 12.75 15.1348 9 15.1348C5.25 15.1348 3 12.4498 3 9.13477" stroke="#032432" strokeWidth="1.125" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
            
            {/* Блок с реквизитами */}
            <div className='bg-[#F5F7F9] rounded-[15px] p-[20px] mt-[20px] overflow-x-hidden'>
              <div className='text-[18px] text-[#032432]'>Реквизиты компании</div>
              <div className='mt-[27px] text-[14px] text-black/60'>{pageVars?.rekv}</div>
              <div className='mt-[10px] text-[14px] text-black/60'>{pageVars?.rekvpodpis}</div>
              
              {/* Динамический список реквизитов из contacts.partnerlist */}
              {(contacts?.partnerlist || []).map((item, index) => (
                <div key={index} className='flex items-center justify-between gap-[9px] mt-[10px]'>
                  <div className='xl:flex-none text-[#032432] text-[16px] xl:text-[18px]'>{item.name}</div>
                  <div className='h-[1px] bg-black/10 flex-auto' />
                  <div className='xl:flex-none text-black/60 text-[14px] xl:text-[16px] tracking-[-0.02em] text-right'>{Parser(item.text)}</div>
                </div>
              ))}
            </div>

            {/* --- Карта со всеми точками --- */}
            <div className='mt-[20px] w-full h-[200px] xl:h-[255px] rounded-[15px] overflow-hidden'>
              {this.renderMap(
                allCoords[0] || [56.234540, 43.454277], // Центрируем на первой точке (Дзержинск) если есть
                10, // Уменьшаем зум, чтобы показать все точки
                mapMarkers,
                '255px',
                'all-contacts'
              )}
            </div>
          </div>
        </div>

        {/* Lightbox для увеличения карт */}
        {lightboxOpen && (
          <div 
            className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90'
            onClick={this.closeLightbox}
          >
            {/* Кнопка закрытия */}
            <button 
              className='absolute top-5 right-5 text-white text-4xl hover:text-gray-300 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 transition-all'
              onClick={this.closeLightbox}
            >
              ×
            </button>

            {/* Изображение */}
            <div 
              className='relative max-w-[90vw] max-h-[90vh] rounded-[15px] overflow-hidden'
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={currentImage} 
                alt={currentImageAlt}
                className='max-w-full max-h-[90vh] object-contain'
              />
              
              {/* Название изображения */}
              <div className='absolute bottom-5 left-1/2 -translate-x-1/2 text-white bg-black bg-opacity-70 px-4 py-2 rounded-full text-sm'>
                {currentImageAlt}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

export default App;